'use client';

/**
 * HubVisitBeacon — invisible client component that fires the Hub CRM visit
 * beacon on mount for the public Profileia Hub page (`/{username}`).
 *
 * Spec ref: docs/SPEC-ANALYTICS-CRM-HUB.md §3.2 (POST /api/public/hub-visit)
 *           + §8 (fire-and-forget with silent error swallow).
 *
 * Why a separate client component (not inline `useEffect` in the page)?
 *   - The /{username} page is a Server Component (SSR + ISR) — Server
 *     Components can't use `useEffect` or browser APIs.
 *   - Same pattern as `<ProfileViewTracker />` (sibling component in
 *     `@/components/hub/profile-view-tracker`) — invisible side-effect only
 *     client component rendered as a child of the Server Component.
 *
 * Anti-P-001 (PII compliance): the beacon payload contains ONLY
 *   { hubOwnerUsername, referer?, ctaClicked? } — NO IP, NO JWT, NO email.
 *   The visitor IP is hashed on the backend (see backend hashVisitorIp) —
 *   the frontend never touches it.
 *
 * Anti-P-041: 0 unsafe any-casts. Anti-P-042: 0 console.* calls.
 */

import { useEffect } from 'react';
import {
  sendHubVisitBeacon,
} from '@/lib/api-client/namespaces/hub-crm';

export interface HubVisitBeaconProps {
  /** The Hub owner's username — identifies which Hub was visited. */
  username: string;
}

/**
 * Render nothing visible. This component is a side-effect-only client
 * component (fires a beacon on mount). Returns `null` so it doesn't affect
 * layout / DOM.
 */
export function HubVisitBeacon({ username }: HubVisitBeaconProps): null {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Fire-and-forget beacon — `sendHubVisitBeacon` uses `keepalive: true`
    // + `.catch(() => {})` so the visitor UX is NEVER affected (analytics
    // is best-effort throughout the stack — see SPEC §8 mitigation).
    sendHubVisitBeacon({
      hubOwnerUsername: username,
      // document.referrer is empty string for direct traffic — normalize
      // to undefined so the backend treats it as "no referer" (null).
      referer:
        typeof document !== 'undefined' && document.referrer.length > 0
          ? document.referrer
          : undefined,
    });
  }, [username]);

  return null;
}
